/**
 * Procedencia — lo que el fichero declara de sí mismo. No lo que es.
 *
 * Es el escaneo del baseline de AWAP 2 (§3.3), suelto: caracteres invisibles y homoglifos,
 * manifiestos C2PA en las imágenes, metadatos del documento (quién, con qué, cuándo, cuánto
 * tiempo de edición) e integridad. La regla de interpretación aparece LITERALMENTE, porque la
 * tentación de leer «cero marcas» como «texto humano» es exactamente lo que hay que cortar.
 *
 * Corre en local con el motor empaquetado: no gasta dinero y el texto no sale del equipo.
 */
import { useEffect, useState } from "react";
import { escaneoProcedencia, miles } from "../api";
import type { Escaneo } from "../api";
import { nombreDeRuta } from "../logica";

const METADATOS: Record<string, string> = {
  creator: "Creado por", lastModifiedBy: "Última modificación por", created: "Creado", modified: "Modificado",
  title: "Título", revision: "Revisión", Application: "Aplicación", AppVersion: "Versión", Company: "Empresa",
  TotalTime: "Tiempo total de edición (min)",
};

const cuenta = (v: unknown): number =>
  Array.isArray(v) ? v.length : typeof v === "number" ? v : v && typeof v === "object" ? Object.keys(v as object).length : 0;

export default function Procedencia({ doc }: { doc?: string | null }) {
  const [r, setR] = useState<{ escaneo: Escaneo; aviso: string } | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    setR(null); setFallo(null);
    if (!doc) return;
    escaneoProcedencia(doc).then(setR).catch((e) => setFallo(String(e.message ?? e)));
  }, [doc]);

  if (!doc) {
    return (
      <section className="carta">
        <h2>Procedencia<span className="sub">sin manuscrito</span></h2>
        <p className="vacio">Elige antes el manuscrito en la sección Manuscrito: el escaneo es de un fichero concreto.</p>
      </section>
    );
  }
  if (fallo) return <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>;
  if (!r) return <section className="carta"><p className="vacio">Escaneando {nombreDeRuta(doc)}…</p></section>;

  const e = r.escaneo;
  if (e.disponible === false) {
    return (
      <section className="carta">
        <h2>Procedencia<span className="sub">{nombreDeRuta(doc)}</span></h2>
        <p className="vacio">No se ha podido escanear: {String(e.motivo ?? "").replace(/\.+$/, "")}.</p>
      </section>
    );
  }

  const invisibles = cuenta(e.invisibles);
  const homoglifos = cuenta(e.homoglifos);
  const imagenes = (e.imagenes ?? []) as { fichero?: string; c2pa?: boolean }[];
  const conC2pa = imagenes.filter((i) => i.c2pa).length;
  const meta = e.metadatos ?? {};
  const textoLeido = (e as { texto?: { leido: boolean; motivo: string | null } }).texto;

  return (
    <>
      <div className="rejilla">
        <section className="carta">
          <h2>Texto<span className="sub">{e.tipo}</span></h2>
          <div className="cifra">
            <b>{textoLeido?.leido === false ? "—" : miles(e.palabras ?? 0)}</b>
            <span>{textoLeido?.leido === false ? "no se pudo leer el texto" : "palabras"}</span>
          </div>
          {textoLeido?.leido === false && <p className="vacio">{textoLeido.motivo}</p>}
        </section>
        <section className="carta">
          <h2>Marcas en el texto<span className="sub">invisibles y homoglifos</span></h2>
          <div className="cifra"><b>{miles(invisibles + homoglifos)}</b><span>{invisibles} invisibles · {homoglifos} homoglifos</span></div>
        </section>
        <section className="carta">
          <h2>Imágenes<span className="sub">manifiesto C2PA</span></h2>
          <div className="cifra"><b>{imagenes.length}</b><span>{conC2pa} con manifiesto (presencia, no validado)</span></div>
        </section>
      </div>

      <section className="carta">
        <h2>Lo que el fichero dice de sí mismo<span className="sub">metadatos</span></h2>
        {Object.keys(meta).length === 0 && <p className="vacio">Este fichero no lleva metadatos legibles.</p>}
        <div className="notas">
          {Object.entries(meta).map(([k, v]) => (
            <div className="nota" key={k}>
              <span className="et">{METADATOS[k] ?? k}</span>
              <b className="mono">{String(v)}</b>
            </div>
          ))}
        </div>
      </section>

      {invisibles > 0 && (
        <section className="carta">
          <h2>Caracteres invisibles<span className="sub">por tipo</span></h2>
          <div className="notas">
            {Object.entries(e.invisibles ?? {}).map(([k, v]) => (
              <div className="nota" key={k}><span className="et">{k}</span><b className="mono">{cuenta(v)}</b></div>
            ))}
          </div>
        </section>
      )}

      <section className="carta">
        <h2>Cómo leer esto<span className="sub">regla de interpretación</span></h2>
        <blockquote>{r.aviso}</blockquote>
        {e.marca_de_texto && <p className="vacio">Marca de texto de los modelos: {e.marca_de_texto.motivo}</p>}
      </section>
    </>
  );
}
